/**
 * usePromptDialog — themed náhrada za window.prompt().
 *
 *   const { ask, dialog } = usePromptDialog();
 *   const name = await ask({ title: t('…'), label: t('…') });
 *   if (name == null) return; // zrušeno
 */
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PromptDialog } from '../components/ui/PromptDialog';

export interface PromptAskOptions {
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  allowEmpty?: boolean;
}

export function usePromptDialog() {
  const { t } = useTranslation();
  const resolveRef = useRef<((v: string | null) => void) | null>(null);
  const [opts, setOpts] = useState<PromptAskOptions | null>(null);

  const close = useCallback((value: string | null) => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setOpts(null);
    r?.(value);
  }, []);

  const ask = useCallback((options: PromptAskOptions) => {
    return new Promise<string | null>((resolve) => {
      if (resolveRef.current) resolveRef.current(null);
      resolveRef.current = resolve;
      setOpts(options);
    });
  }, []);

  const dialog = (
    <PromptDialog
      open={!!opts}
      title={opts?.title ?? ''}
      description={opts?.description}
      label={opts?.label}
      placeholder={opts?.placeholder}
      defaultValue={opts?.defaultValue ?? ''}
      confirmLabel={opts?.confirmLabel}
      cancelLabel={opts?.cancelLabel ?? t('ui.cancel')}
      allowEmpty={opts?.allowEmpty}
      onConfirm={(v) => close(v)}
      onCancel={() => close(null)}
    />
  );

  return { ask, dialog, open: !!opts };
}
